import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';

import { convertUnits } from '@acceligentllc/shared/utils/unitConversion';

import { QuantityUnitMap } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

const BILLING_TYPE_SPECIFIC_COLUMN_VALUES = {
	[WorkSummaryBillingType.BILLABLE_WORK]: (_ws: WorkSummaryVM) => [
		_ws.customerId ?? 'N/A',
		_ws.billingGroup ?? 'N/A',
		_ws.unitPrice ? `${parseFloat(_ws.unitPrice.toFixed(4))}` : 'N/A',
		_calculateUnitPricePerQuantityUnit(_ws),
		_ws.description ?? 'N/A',
		_ws.total ? (_ws.total + '') : 'N/A',
	],
	[WorkSummaryBillingType.MATERIAL_COST]: (_ws: WorkSummaryVM) => [
		_ws.materialDisplayName ?? 'N/A',
		_ws.unitMaterialCost ? `${parseFloat(_ws.unitMaterialCost.toFixed(4))}` : 'N/A',
		_ws.description ?? 'N/A',
		_ws.materialCost ? (_ws.materialCost + '') : 'N/A',
	],
};

enum WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS {
	WORK = 'WORK',
	TYPE = 'TYPE',
	DEFINITION_FIELD = 'DEFINITION_FIELD',
	QUANTITY = 'QUANTITY',
	UNIT = 'UNIT',
	BILLING_CODE = 'BILLING_CODE',
	BILLING_GROUP = 'BILLING_GROUP',
	UNIT_PRICE = 'UNIT_PRICE',
	QUANTITY_UNIT_PRICE = 'QUANTITY_UNIT_PRICE',
	DESCRIPTION = 'DESCRIPTION',
	REVENUE = 'REVENUE',
	JOB_NAME = 'JOB_NAME',
	MATERIAL = 'MATERIAL',
	MATERIAL_UNIT_COST = 'MATERIAL_UNIT_COST',
	MATERIAL_COST = 'MATERIAL_COST',
}

const WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADER_LABELS = {
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.WORK]: 'Work',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.TYPE]: 'Type',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.DEFINITION_FIELD]: 'Definition',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.QUANTITY]: 'Quantity',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.UNIT]: 'Unit',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.BILLING_CODE]: 'Billing code',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.BILLING_GROUP]: 'Billing group',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.UNIT_PRICE]: 'Unit Price per billingCode Unit ($)',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.QUANTITY_UNIT_PRICE]: 'Unit Price per quantity Unit ($)',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.DESCRIPTION]: 'Description',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.REVENUE]: 'Revenue ($)',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.JOB_NAME]: 'Job Name',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.MATERIAL]: 'Material',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.MATERIAL_UNIT_COST]: 'Unit Cost per Material Cost Unit ($)',
	[WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.MATERIAL_COST]: 'Material Cost ($)',
};

const TABLE_HEADERS_FOR_COST_SOURCE = {
	[WorkSummaryBillingType.BILLABLE_WORK]: [
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.WORK,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.TYPE,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.DEFINITION_FIELD,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.QUANTITY,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.UNIT,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.BILLING_CODE,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.BILLING_GROUP,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.UNIT_PRICE,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.QUANTITY_UNIT_PRICE,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.DESCRIPTION,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.REVENUE,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.JOB_NAME,
	],
	[WorkSummaryBillingType.MATERIAL_COST]: [
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.WORK,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.TYPE,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.DEFINITION_FIELD,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.QUANTITY,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.UNIT,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.MATERIAL,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.MATERIAL_UNIT_COST,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.DESCRIPTION,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.MATERIAL_COST,
		WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.JOB_NAME,
	],
};

export const toCSVData = (workSummaries: WorkSummaryVM[], belongsToProject: boolean, billingType: WorkSummaryBillingType): string[][] => {
	const maxNumberOfDefinitionFields = workSummaries.reduce(maxDefinitionFieldsReducer, 0);
	const headers = resolveWorkSummaryBillableWorkTableHeaders(maxNumberOfDefinitionFields, belongsToProject, billingType);

	const rows = workSummaries.map((_ws) => {

		const adjustedDefFields: string[] = [];
		for (let i = 0; i < maxNumberOfDefinitionFields; i++) {
			const defFieldName = _ws.definitionFields[i]?.fieldName ? `${_ws.definitionFields[i]?.fieldName}:` : '';
			const defFieldValue = _ws.definitionFields[i] ? _resolveFieldValue(_ws.definitionFields[i].value, _ws.definitionFields[i].fieldType) : 'N/A';
			const defFieldUnit = _ws.definitionFields[i]?.fieldUnit ? ` ${_ws.definitionFields[i]?.fieldUnit}` : '';
			const defValue = `${defFieldName} ${defFieldValue}${defFieldUnit}`;
			adjustedDefFields.push(defValue);
		}

		const workTypeAdjustedName = _ws.typeFieldType === ReportBlockFieldEnum.Type.NUMERIC_ATTRIBUTE
			? _ws.typeFieldName
			: `${_ws.typeFieldName}: ${_resolveFieldValue(_ws.type, _ws.typeFieldType)}`;

		const row = [
			_ws.work,
			_ws.type ? workTypeAdjustedName : 'N/A',
			...adjustedDefFields,
			_ws.quantity + '',
			_ws.unit ?? '',
			...BILLING_TYPE_SPECIFIC_COLUMN_VALUES[billingType](_ws),
		];
		if (belongsToProject) {
			row.push(_ws.jobCode ?? 'N/A');
		}
		return row;
	});

	return [headers, ...rows];
};

const resolveWorkSummaryBillableWorkTableHeaders = (
	maxNumberOfDefinitionFields: number,
	belongsToProject: boolean,
	billingType: WorkSummaryBillingType
) => {
	const headers: string[] = [];
	const headerTemplate = WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS;
	const headerTemplateKeys = TABLE_HEADERS_FOR_COST_SOURCE[billingType];
	for (const headerTemplateKey of headerTemplateKeys) {
		if (headerTemplate[headerTemplateKey] === headerTemplate.DEFINITION_FIELD) {
			for (let i = 0; i < maxNumberOfDefinitionFields; i++) {
				headers.push(`${WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADER_LABELS[headerTemplate[headerTemplateKey]]} ${i + 1}`);
			}
		} else {
			if (belongsToProject || headerTemplate[headerTemplateKey] !== WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADERS.JOB_NAME) {
				headers.push(WORK_SUMMARY_BILLABLE_WORK_TABLE_HEADER_LABELS[headerTemplate[headerTemplateKey]]);
			}
		}
	}

	return headers;
};

function _resolveFieldValue(value: string, fieldType: ReportBlockFieldEnum.Type) {
	let resolvedValue = value;

	if (fieldType === ReportBlockFieldEnum.Type.BOOLEAN) {
		resolvedValue = new Boolean(value) ? 'Yes' : 'No';
	}

	if (fieldType === ReportBlockFieldEnum.Type.ADDRESS) {
		return (value as unknown as { street?: string; })?.street ?? null;
	}

	if (fieldType === ReportBlockFieldEnum.Type.EQUIPMENT) {
		const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
		return `${parsedValue.code ?? ''} ${parsedValue.specification ? `(${parsedValue.specification})` : ''}`;
	}

	return resolvedValue;
}

const maxDefinitionFieldsReducer = (_max: number, _currentWS: WorkSummaryVM) => {
	if (_currentWS.definitionFields?.length > _max) {
		_max = _currentWS.definitionFields?.length;
	}

	return _max;
};

const _calculateUnitPricePerQuantityUnit = (ws: WorkSummaryVM) => {
	if (!ws.unitPrice || !ws.unit || !ws?.billingCodeUnit) {
		return '';
	}

	if (QuantityUnitMap[ws.unit] === QuantityUnitMap[ws.billingCodeUnit]) {
		return `${parseFloat(ws.unitPrice.toFixed(4))}`;
	}

	const convertedUnit = convertUnits(ws.unitPrice, QuantityUnitMap[ws.unit], QuantityUnitMap[ws.billingCodeUnit]);

	return convertedUnit ? `${parseFloat(convertedUnit.toFixed(8))}` : '';
};

function extractObjectValue(value: Nullable<string>) {
	let parsed: unknown = value;

	if (typeof value === 'string') {
		try {
			parsed = JSON.parse(value);
		} catch {
			return value;
		}
	}

	if (typeof parsed === 'object' && parsed !== null && 'id' in parsed) {
		return parsed.id;
	}

	return value;
}

function getDefinitionFieldValue(definitionField: { value: Nullable<string>; index: number; }) {
	return extractObjectValue(definitionField.value);
}

function sortDefinitionFields(_a: { index: number; }, _b: { index: number; }) {
	return _a.index - _b.index;
}

interface ResolveHighlightGroupKey {
	billingType: WorkSummaryBillingType;
	subJobId: number;
	billableWorkId: number;
	workType: Nullable<string>;
	definitionFields: { value: Nullable<string>; index: number; }[];
}

export function resolveHighlightGroupKey(event: ResolveHighlightGroupKey) {
	const { billingType, billableWorkId, workType, definitionFields, subJobId } = event;

	return JSON.stringify([
		billingType,
		billableWorkId,
		extractObjectValue(workType),
		[...definitionFields].sort(sortDefinitionFields).map(getDefinitionFieldValue),
		subJobId,
	]);
}
